import { filter, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { RouterLink } from '@angular/router';
import { AuthService } from '@auth/services/auth/auth.service';
import { LayoutService } from '@layout/services/layout/layout.service';
import { DesktopOrgUsersTableComponent } from '@organisations/components/desktop-org-users-table/desktop-org-users-table.component';
import { MobileOrgUsersTableComponent } from '@organisations/components/mobile-org-users-table/mobile-org-users-table.component';
import { IUserAndOrganisationUser, OrganisationMemberStatus, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { IOrganisation } from '@organisations/models/organisations';
import { OrganisationRoutes } from '@organisations/organisation.routes';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { OrganisationUserService } from '@organisations/services/organisation-user/organisation-user.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { UnauthorisedBannerComponent } from '@shared/components/ui/unauthorised-banner/unauthorised-banner.component';

@Component({
	selector: 'app-organisation-users',
	imports: [
		CommonModule,
		RouterLink,
		UnauthorisedBannerComponent,
		DesktopOrgUsersTableComponent,
		MobileOrgUsersTableComponent,
		LoadingStateComponent,
	],
	templateUrl: './organisation-users.component.html',
	styleUrl: './organisation-users.component.scss',
})
export class OrganisationUsersComponent implements OnInit, OnDestroy {
	destroyed$ = new EventEmitter<void>();
	organisation: IOrganisation;
	isMobileSize = true;
	userHasAccess = false;
	isLoading = true;

	OrganisationRoutes = OrganisationRoutes;
	private layoutService = inject(LayoutService);
	private organisationUserService = inject(OrganisationUserService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private authService = inject(AuthService);

	organisationMembers: Array<IUserAndOrganisationUser> = [];

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		this.subscribeToSelectedOrganisation();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	subscribeToSelectedOrganisation(): void {
		this.organisationSelectedService.selectedOrganisation
			.pipe(
				takeUntil(this.destroyed$),
				filter((o) => o !== null),
			)
			.subscribe((selectedOrg) => {
				if (
					[OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(
						selectedOrg?.organisationUser.accessLevel,
					)
				) {
					this.userHasAccess = true;
					this.organisation = selectedOrg.organisation;
					this.subscribeToOrganisationUsers(selectedOrg.organisationUser.organisation);
				} else {
					this.userHasAccess = false;
					this.isLoading = false;
				}
			});
	}

	subscribeToOrganisationUsers(orgRef: DocumentReference): void {
		this.organisationUserService
			.visibleUsersByOrganisation(orgRef)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((organisationUsers) => {
				const user = this.authService.currentUser;
				this.organisationMembers = organisationUsers.map((orgUser) => {
					const isCurrentUser = user?.uid === orgUser.user.id;
					return {
						...orgUser,
						organisationUser: {
							...orgUser.organisationUser,
							status: isCurrentUser ? OrganisationMemberStatus.CURRENTLY_LOGGED_IN : orgUser.organisationUser.status,
						},
					};
				});
				this.organisationMembers = organisationUsers.sort((a, b) => {
					const nameA = a.user.displayName?.toLowerCase() || '';
					const nameB = b.user.displayName?.toLowerCase() || '';
					return nameA.localeCompare(nameB);
				});
				this.isLoading = false;
			});
	}
}
